/**
 * Klient för Freja eID bind/unbind i Lyktan Settings (S-04-37).
 *
 * Alla muterande anrop bär CSRF-header (S-12-11c) och kräver aktiv
 * admin-session. bind/initiate + unbind kräver dessutom TOTP-step-up
 * (koden skickas i body, samma mönster som admin-users-mutationer).
 * Inga tokens eller secrets lagras klient-side.
 */
import { csrfHeader } from './csrf';

export type FrejaStatusResult =
  | { kind: 'ok'; bound: boolean }
  | { kind: 'disabled' }
  | { kind: 'failure'; message: string };

export type FrejaBindInitiateResult =
  | { kind: 'started'; authRef: string; qrData: string }
  | { kind: 'failure'; message: string };

export type FrejaBindPollResult =
  | { kind: 'bound' }
  | { kind: 'pending'; status: string }
  | { kind: 'failure'; message: string };

export type FrejaUnbindResult = { kind: 'ok' } | { kind: 'failure'; message: string };

const API_BASE = '/api/admin/auth/freja';

function messageForError(code: string): string {
  switch (code) {
    case 'invalid_mfa_code':
      return 'Felaktig MFA-kod. Försök igen.';
    case 'mfa_locked':
      return 'För många misslyckade försök. Vänta en stund och försök igen.';
    case 'mfa_not_configured':
      return 'Tvåfaktor måste vara aktiverad innan Freja kan kopplas.';
    case 'freja_upstream_error':
      return 'Freja-tjänsten svarar inte just nu. Försök igen om en stund.';
    case 'freja_not_approved':
      return 'Kopplingen godkändes inte eller hann gå ut. Börja om.';
    case 'freja_level_too_low':
      return 'Ditt Freja eID har inte tillräcklig registreringsnivå. Du behöver Freja eID Plus.';
    case 'freja_pseudonym_taken':
      return 'Detta Freja eID är redan kopplat till ett annat admin-konto.';
    case 'freja_unknown_transaction':
      return 'Kopplingen kunde inte slutföras. Börja om från MFA-koden.';
    case 'user_deactivated':
      return 'Sessionen har gått ut. Logga in igen.';
    case 'network':
      return 'Kunde inte nå servern. Kontrollera anslutningen.';
    default:
      return 'Något gick fel. Försök igen om en stund.';
  }
}

async function safeJson(response: Response): Promise<{ error?: string } | null> {
  try {
    return (await response.json()) as { error?: string };
  } catch {
    return null;
  }
}

export async function frejaStatus(): Promise<FrejaStatusResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/status`, { credentials: 'include' });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }
  if (response.status === 503) {
    return { kind: 'disabled' };
  }
  if (response.ok) {
    const body = (await response.json()) as { bound?: boolean };
    return { kind: 'ok', bound: body.bound === true };
  }
  const body = await safeJson(response);
  return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
}

export async function frejaBindInitiate(mfaCode: string): Promise<FrejaBindInitiateResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/bind/initiate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body: JSON.stringify({ mfaCode }),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }
  if (response.ok) {
    const body = (await response.json()) as { authRef?: string; qrData?: string };
    if (typeof body.authRef === 'string' && typeof body.qrData === 'string') {
      return { kind: 'started', authRef: body.authRef, qrData: body.qrData };
    }
    return { kind: 'failure', message: messageForError('default') };
  }
  const body = await safeJson(response);
  return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
}

export async function frejaBindPoll(authRef: string): Promise<FrejaBindPollResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/bind/poll`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body: JSON.stringify({ authRef }),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }
  if (response.status === 202) {
    const body = (await response.json()) as { status?: string };
    return { kind: 'pending', status: body.status ?? 'STARTED' };
  }
  if (response.ok) {
    return { kind: 'bound' };
  }
  const body = await safeJson(response);
  return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
}

export async function frejaUnbind(mfaCode: string): Promise<FrejaUnbindResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/unbind`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...csrfHeader() },
      body: JSON.stringify({ mfaCode }),
    });
  } catch {
    return { kind: 'failure', message: messageForError('network') };
  }
  if (response.ok || response.status === 204) {
    return { kind: 'ok' };
  }
  const body = await safeJson(response);
  return { kind: 'failure', message: messageForError(body?.error ?? 'default') };
}
